/**
 * Manager Access Request Popup
 * 
 * Displays pending manager access requests to company owners
 * Allows approving or denying requests with reason
 */

import React, { useState, useEffect } from 'react';
import { X, CheckCircle, XCircle, AlertCircle, User, Building2, Phone, Mail } from 'lucide-react';
import { ManagerAccessRequest } from '../types/managerAccess.types';
import { 
  getPendingManagerRequests,
  approveManagerAccessRequest,
  denyManagerAccessRequest
} from '../services/managerAccessService';

interface ManagerAccessRequestPopupProps {
  userId: string;
  onClose?: () => void;
}

export const ManagerAccessRequestPopup: React.FC<ManagerAccessRequestPopupProps> = ({
  userId,
  onClose
}) => {
  const [requests, setRequests] = useState<ManagerAccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentRequestIndex, setCurrentRequestIndex] = useState(0);
  const [showDenyModal, setShowDenyModal] = useState(false);
  const [denyReason, setDenyReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadRequests();
  }, [userId]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const pendingRequests = await getPendingManagerRequests(userId);
      setRequests(pendingRequests);
    } catch (error) {
      console.error('Error loading manager requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId: string) => {
    if (processing) return;
    
    const confirmed = window.confirm(
      '✅ Weet je zeker dat je deze manager toegang wilt verlenen? De gebruiker krijgt toegang tot je bedrijfsprofiel.'
    );

    if (!confirmed) return;

    try {
      setProcessing(true);
      await approveManagerAccessRequest(requestId, userId);
      
      // Remove from list
      setRequests(prev => prev.filter(r => r.id !== requestId));
      
      // Show success message
      alert('✅ Manager toegang goedgekeurd! De gebruiker heeft een notificatie ontvangen.');
      
      // Move to next request or close
      if (requests.length === 1) {
        onClose?.();
      } else {
        setCurrentRequestIndex(prev => Math.min(prev, requests.length - 2));
      }
    } catch (error) {
      console.error('Error approving request:', error);
      alert('❌ Er is een fout opgetreden. Probeer het opnieuw.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeny = async (requestId: string) => {
    if (!denyReason.trim()) {
      alert('⚠️ Geef een reden op voor het afwijzen van de aanvraag.');
      return;
    }

    if (processing) return;

    try {
      setProcessing(true);
      await denyManagerAccessRequest(requestId, userId, denyReason);
      
      // Remove from list
      setRequests(prev => prev.filter(r => r.id !== requestId));
      
      // Show success message
      alert('✅ Aanvraag afgewezen. De gebruiker heeft een notificatie ontvangen.');
      
      // Reset and move to next
      setDenyReason('');
      setShowDenyModal(false);
      
      if (requests.length === 1) {
        onClose?.();
      } else {
        setCurrentRequestIndex(prev => Math.min(prev, requests.length - 2));
      }
    } catch (error) {
      console.error('Error denying request:', error);
      alert('❌ Er is een fout opgetreden. Probeer het opnieuw.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Aanvragen laden...</p>
        </div>
      </div>
    );
  }

  if (requests.length === 0) {
    return null;
  }

  const currentRequest = requests[currentRequestIndex];

  if (showDenyModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-6 max-w-md w-full">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">Aanvraag afwijzen</h3>
            <button
              onClick={() => {
                setShowDenyModal(false);
                setDenyReason('');
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <p className="text-sm text-amber-800">
                  Geef een duidelijke reden waarom je deze aanvraag afwijst. Dit wordt gedeeld met de aanvrager.
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reden voor afwijzing *
              </label>
              <textarea
                value={denyReason}
                onChange={(e) => setDenyReason(e.target.value)}
                placeholder="Bijv. We werken momenteel niet met externe managers..."
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDenyModal(false);
                  setDenyReason('');
                }}
                disabled={processing}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Annuleren
              </button>
              <button
                onClick={() => handleDeny(currentRequest.id)}
                disabled={processing || !denyReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processing ? 'Bezig...' : 'Afwijzen'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-xl">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <AlertCircle className="w-6 h-6" />
                <h2 className="text-2xl font-bold">Manager Toegangsverzoek</h2>
              </div>
              <p className="text-blue-100">
                Er zijn {requests.length} nieuwe aanvra{requests.length === 1 ? 'ag' : 'gen'} voor manager toegang
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Request Counter */}
          {requests.length > 1 && (
            <div className="flex justify-center space-x-2">
              {requests.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full ${
                    index === currentRequestIndex ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Requester Info */}
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Aanvrager Informatie</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center space-x-3">
                  <User className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Naam</p>
                    <p className="font-medium text-gray-900">{currentRequest.requesterName}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">E-mail</p>
                    <p className="font-medium text-gray-900">{currentRequest.requesterEmail}</p>
                  </div>
                </div>
                {currentRequest.requesterPhone && (
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Telefoon</p>
                      <p className="font-medium text-gray-900">{currentRequest.requesterPhone}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Company Info */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Bedrijfsgegevens</h3>
              <div className="bg-blue-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center space-x-3">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-blue-700">Bedrijfsnaam</p>
                    <p className="font-medium text-blue-900">{currentRequest.companyName}</p>
                  </div>
                </div>
                {currentRequest.companyKvK && (
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5" />
                    <div>
                      <p className="text-sm text-blue-700">KVK-nummer</p>
                      <p className="font-medium text-blue-900">{currentRequest.companyKvK}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Message */}
            {currentRequest.message && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Bericht</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700 whitespace-pre-wrap">{currentRequest.message}</p>
                </div>
              </div>
            )}

            {/* Info Box */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>💡 Let op:</strong> Als je deze aanvraag goedkeurt, krijgt deze persoon toegang als manager 
                voor je bedrijf. Je kunt deze toegang altijd later intrekken.
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-gray-50 p-6 rounded-b-xl border-t flex space-x-3">
          <button
            onClick={() => setShowDenyModal(true)}
            disabled={processing}
            className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 border-2 border-red-500 text-red-600 rounded-lg hover:bg-red-50 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <XCircle className="w-5 h-5" />
            <span>Afwijzen</span>
          </button>
          <button
            onClick={() => handleApprove(currentRequest.id)}
            disabled={processing}
            className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CheckCircle className="w-5 h-5" />
            <span>{processing ? 'Bezig...' : 'Goedkeuren'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManagerAccessRequestPopup;
